import { ReactNode, useEffect, useRef } from 'react';
import { useStackedCardWizardContext } from './stacked-card-wizard-context';
import { useStepIndexContext } from './step-index-context';

type Props = {
  stepId: string;
  title?: ReactNode;
  children: ReactNode;
  canGoNext?: boolean;
  onNext?: () => void | Promise<void>;
};

export const StackedCardWizardStep = ({ stepId: _stepId, title, children, onNext, canGoNext = true }: Props) => {
  const { currentStep, setCanGoNext, setStepTitle, registerStepCallback, unregisterStepCallback } =
    useStackedCardWizardContext();
  const stepIndex = useStepIndexContext();

  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;

  // Set step title if provided
  useEffect(() => {
    if (title !== undefined) {
      setStepTitle(stepIndex, title);
    }
  }, [stepIndex, title, setStepTitle]);

  // Update canGoNext for the current active step
  useEffect(() => {
    if (stepIndex === currentStep) {
      setCanGoNext(canGoNext);
    }
  }, [canGoNext, currentStep, setCanGoNext, stepIndex]);

  // Register the onNext callback for this step (only when stepIndex changes or onNext becomes defined/undefined)
  useEffect(() => {
    if (onNext) {
      const wrappedCallback = async () => {
        if (onNextRef.current) {
          return await onNextRef.current();
        }
      };
      registerStepCallback(stepIndex, wrappedCallback);

      return () => {
        unregisterStepCallback(stepIndex);
      };
    }

    // Return undefined when onNext is not provided
    return undefined;
  }, [stepIndex, !!onNext, registerStepCallback, unregisterStepCallback]);

  return <>{children}</>;
};
